import { Component, EventEmitter, forwardRef, OnDestroy, OnInit, Output, Input } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { CompanyService } from 'src/app/_service/company.service';
import { VesselService } from 'src/app/_service/vessel.service';
import { TenantService } from 'src/app/_service/tenant.service';
import { AuthService } from 'src/app/_service/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { postTenantData, tenantForm, rbac } from 'src/app/_model/tenant';
import { getCompanyData } from 'src/app/_model/company';
import { getVesselData } from 'src/app/_model/vessel';
import { RbacEditComponent } from '../rbac-edit/rbac-edit.component';

@Component({
  selector: 'app-tenant-edit',
  templateUrl: './tenant-edit.component.html',
  styleUrl: './tenant-edit.component.css'
})
export class TenantEditComponent implements OnInit, OnDestroy {
  @Output() dataChange = new EventEmitter();
  form = new tenantForm;
  tenantForm = this.form.defaultForm;
  tenantData!: postTenantData;
  isSubmitting = false;
  selectedRids: number[] = [0];
  vslDict: { [key: string]: {name: string, vessels: any[]}} = {}
  currSelectedCompany: string = '';
  currSelectedVessels = new Set<string>();
  rbacEditable = true;

  constructor(
    private location: Location,
    private companyService: CompanyService,
    private vesselService: VesselService,
    private tenantService: TenantService,
    private translate: TranslateService,
    public router: Router,
    public dialog: MatDialog,
    public auth: AuthService,
  ) {
    this.companyList = this.companyService.companyList;
    this.vesselList = this.vesselService.vesselList;
  }

  roleOptions = [
    { label: "globalAdmin", id: 3, alwaysChecked: true, },
    { label: "admin", id: 0, alwaysChecked: true, },
    { label: "user", id: 1, alwaysChecked: false, },
    { label: "thirdParty", id: 2, alwaysChecked: false, },
  ];

  companyList: getCompanyData[];
  vesselList: getVesselData[];

  ngOnInit(): void {
    this.tenantForm.patchValue(this.tenantService.editTenantData);
    if (this.tenantService.editTenantData?.rbac?.length > 1) this.rbacEditable = false;
    else if (this.tenantService.editTenantData?.rbac?.length === 1) {
      this.parseRBAC(this.tenantService.editTenantData?.rbac);
    }
  }

  parseRBAC(rbac: any) {
    this.selectedRids = [...new Set([...this.selectedRids, ...rbac[0]?.rids])];

    rbac[0]?.mgr.forEach((company_id: string) => {
      const currVesselList = this.vesselList.filter(vessel => vessel.manager_id === company_id);
      this.vslDict[company_id] = {
        name: this.companyList.find(company => company.id === company_id)?.name || company_id,
        vessels: currVesselList?.filter(vessel => rbac[0]?.vsl.includes(vessel.id)).map(vessel => ({ id: vessel.id, name: vessel.name })),
      };
    });
  }

  ngOnDestroy(): void {
    this.tenantService.selectedTenantID = '';
    this.tenantForm.reset();
  }

  onRoleSelectionChange(event: any, id: number) {
    if (event.target.checked) {
      this.selectedRids.push(id);
    } else {
      this.selectedRids = this.selectedRids.filter(rid => rid !== id);
    }
  }

  addCompany() {
    const dialogRef = this.dialog.open(RbacEditComponent, {
      width:'798px',
      height: '460px',
      data: { selectedCompanies: Object.keys(this.vslDict) },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result)
        this.vslDict[result?.currSelectedCompany?.id] = {name: result?.currSelectedCompany?.name, vessels: result?.selectedVesselList};
    });
  }

  getSelectedCompanies() {
    return Object.entries(this.vslDict).map(([id, { name }]) => ({
      id,
      name,
    }));
  }

  removeVessel(companyId: string, vesselId: string) {
    if (this.vslDict[companyId]) {
      this.vslDict[companyId].vessels = this.vslDict[companyId].vessels.filter(vessel => vessel.id != vesselId);
    }
  }

  removeCompany(companyId: string) {
    delete this.vslDict[companyId];
  }

  onSubmit() {
    if(this.checkSubmit()){
      if (this.tenantForm.value?.expiration_date == "")
        this.tenantForm.patchValue({expiration_date: null});
      const allVsl = [];
      for (const companyId in this.vslDict) {
        if (this.vslDict[companyId].vessels) {
          const currIds = this.vslDict[companyId].vessels.map(v => v.id);
          allVsl.push(...currIds);
        }
      }
      const currRbac: rbac[] = [{
        mgr: Object.keys(this.vslDict),
        vsl: allVsl,
        rids: this.selectedRids,
      }];
      if (this.rbacEditable)
        this.tenantForm.patchValue({rbac: currRbac});
      
      this.tenantService.updateTenant(this.tenantForm.value)
        .subscribe({
          error: () => {
            window.alert(this.translate.instant("err"))
          },
          complete: () => {
            window.alert(this.translate.instant("updated"));
            this.location.back();
            this.tenantForm.reset();
            this.updateTenantList();
            this.dataChange.emit;
          }
        });
    }
  }

  updateTenantList() {
    this.tenantService.tenantList = [];
  }

  deleteTenant() {
    if (this.auth.isYakuin) {
      window.alert(this.translate.instant("noPermission"));
      return;
    }
    if (this.checkDelete()) {
      this.tenantService.deleteTenant()
      .subscribe({
        error: () => {
          window.alert(this.translate.instant("err"));
        },
        complete: () => {
          window.alert(this.translate.instant("deleted"));
          this.tenantForm.reset();
          this.location.back();
          this.updateTenantList();
          this.dataChange.emit;
        }
      });
    }
  }

  checkDelete() {
    if (window.confirm('Are you sure you want to delete this tenant?')) {
      return true;
    } else {
      return false;
    }
  }

  checkSubmit() {
    if(window.confirm('Can I register this?')){
      return true;
    }else{
      return false;
    }
  }

  goBack(){
    this.location.back();
  }
}
